import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/**
 * Desktop app only: the owner resets a member's password (the desktop app
 * can't send "forgot password" emails). Leave the box empty to generate a
 * temporary password, shown once so it can be passed on.
 */
export function ResetMemberPasswordDialog({ open, onOpenChange, orgId, member }) {
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setCustom(""); setResult(null); } }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (custom && custom.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/orgs/${orgId}/members/${member.id}/reset-password`,
        custom ? { new_password: custom } : {});
      setResult(r.data.password);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result); toast.success("Password copied"); }
    catch { toast.error("Couldn't copy — select it and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
        {!result ? (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Reset password</DialogTitle>
              <DialogDescription>
                Set a new password for <b>{member?.email}</b>. They'll be signed out and can
                log in with the new password, then change it under their profile.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="new-pw">New password <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="new-pw" type="text" autoComplete="off" value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Leave empty to generate one" data-testid="reset-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="reset-password-submit">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Reset password
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>New password set</DialogTitle>
              <DialogDescription>
                Give this to <b>{member?.email}</b>. It won't be shown again.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 flex items-center gap-2">
              <code className="flex-1 rounded-md border border-border bg-muted px-3 py-2 font-mono text-base tracking-wide select-all"
                data-testid="reset-password-result">{result}</code>
              <Button type="button" variant="outline" size="icon" onClick={copy} aria-label="Copy password">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="reset-password-done">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
